/* eslint-disable max-lines */

import type { DateRangeValue } from '@core/date-range-types';
import type { IntegrationsElement } from '@core/integrations-types';
import { DisqualifyKind } from '@core/search-types';
import type {
  FullSearchAggregationsV2,
  FullSearchCandidateRecord,
  FullSearchNoteRecord,
  SearchSegment,
} from '@core/search-types';
import type { IconName } from '@core/user-interface-legacy';
import { objectValues } from '@core/utils-legacy';

import type { SortByType } from './candidates-table.types';
import { ViewType } from './candidates-table.types';

export type {
  CandidateDataTableColumn,
  CandidateDataTableColumnWithVisibility,
} from './candidates-table.types';
export { ViewType, SortByType, CandidateColumnId } from './candidates-table.types';

export type FullSearchResult = FullSearchCandidateRecord | FullSearchNoteRecord;
export type CandidateSearchStoreId = 'search';

export type RecordsData = {
  records: FullSearchResult[];
  aggregations: FullSearchAggregationsV2;
  total: number;
  elements: ElementsPerCandidate;
};

export type ElementsPerCandidate = {
  [candidateId: number]: IntegrationsElement[];
};

export type SegmentsCountsData = Array<{ id: number; segmentType: string; count: number }>;
export type ViewsCountsData = Array<{ view: ViewType; count: number | null }>;
export type ToggleSegmentDropdownEvent = {
  open: boolean;
  location: 'sidebar' | 'current';
  segment?: SearchSegment;
};

export function isCandidatesView(view: ViewType): boolean {
  return [
    ViewType.Candidates,
    ViewType.GdprExpiringCandidates,
    ViewType.GdprRemovalCandidates,
    ViewType.GdprCorrectionCandidates,
    ViewType.GdprAwaitingConsentCandidates,
    ViewType.RecentlyDeleted,
    ViewType.NoEEOData,
    ViewType.EEODataRequested,
    ViewType.Hires,
  ].includes(view);
}

export function isDisqualifiedView(view: ViewType): boolean {
  return view === ViewType.DisqualifiedCandidates;
}

export function isEEOView(view: ViewType): boolean {
  return view === ViewType.NoEEOData || view === ViewType.EEODataRequested;
}

export function isRecentlyDeletedView(view: ViewType): boolean {
  return view === ViewType.RecentlyDeleted;
}

export function isHiresView(view: ViewType): boolean {
  return ViewType.Hires === view;
}

export type AnyFilterValue = '__any';
export const AnyFilterValue: AnyFilterValue = '__any';
export type AllFilterValue = '__all';
export const AllFilterValue: AllFilterValue = '__all';
export type AnyFollowedFilterValue = '__any_followed';
export const AnyFollowedFilterValue: AnyFollowedFilterValue = '__any_followed';

export enum StatusFilterProp {
  Qualified = 'qualified',
  Disqualified = 'disqualified',
  New = 'new',
  Overdue = 'overdue',
}

export const StatusFilterProps = objectValues(StatusFilterProp);

export enum GdprStatusFilterProp {
  NoConsent = 'no_consent',
  ConsentGiven = 'consent_given',
  ConsentRequested = 'consent_requested',
  Expiring = 'expiring',
  Expired = 'expired',
}

export const GdprStatusFilterProps = objectValues(GdprStatusFilterProp);

export enum SourceFilterProp {
  CareerSite = 'career_site',
  Email = 'email',
  Manual = 'manual',
  Import = 'import',
  Referral = 'referral',
}

export const SourceFilterProps = objectValues(SourceFilterProp);

export enum EventTypeFilterProp {
  OnSiteInterview = 'onsite',
  PhoneInterview = 'call',
  Meeting = 'meeting',
  VideoInterview = 'video',
}

export const EventTypeFilterProps = objectValues(EventTypeFilterProp);

export enum EventTypeNotFilterProp {
  OnSiteInterview = 'not_onsite',
  PhoneInterview = 'not_call',
  Meeting = 'not_meeting',
  VideoInterview = 'not_video',
}

export const EventTypeNotFilterProps = objectValues(EventTypeNotFilterProp);

export type NumberFilterValue = {
  type: 'has_any' | 'has_none' | 'range' | 'has_hidden' | null;
  min?: number | null;
  max?: number | null;
};

export type Range = {
  min?: string;
  max?: string;
};

export type NumberRangeProps = Range & {
  currency?: string;
};

export enum GenderFilterProp {
  Male = 'male',
  Female = 'female',
  NonBinary = 'non_binary',
  Other = 'other',
}

export const GenderFilterProps = objectValues(GenderFilterProp);

export enum NotGenderFilterProp {
  Male = 'not_male',
  Female = 'not_female',
  NonBinary = 'not_non_binary',
  Other = 'not_other',
}

export const NotGenderFilterProps = objectValues(NotGenderFilterProp);

export enum JobStatusFilterProp {
  Published = 'published',
  Internal = 'internal',
  Closed = 'closed',
  Archived = 'archived',
}

export const JobStatusFilterProps = objectValues(JobStatusFilterProp);

export enum HiringStatusFilterProp {
  Hired = 'hired',
  Active = 'active',
}

export const HiringStatusFilterProps = objectValues(HiringStatusFilterProp);

export enum HiringDateFilterProp {
  HiredAt = 'hired_at',
  JobStartsAt = 'job_starts_at',
}

export const HiringDateFilterProps = objectValues(HiringDateFilterProp);

export enum EEOStatusFilterProp {
  EEOCollected = 'completed',
  EEORequested = 'requested',
  EEONoData = 'no_eeo_data',
}

export const EEOStatusFilterProps = objectValues(EEOStatusFilterProp);

export const DisqualifyKindFilterProps = objectValues(DisqualifyKind);

export type BelongsToRelationFilterValue = string | number | null;
export type HasManyRelationFilterValue = Array<string | number | AnyFilterValue>;
export type HasManyOffersRelationFilterValue = Array<
  number | AnyFilterValue | AnyFollowedFilterValue
>;
export type OpenQuestionsFilterValue = Array<{
  questionHash: string;
  answerHashes: string[];
}>;
export type QuestionnaireAnswersFilterValue = Array<{
  questionHash: string;
  answerHashes: string[];
}>;
export type JobLocationFilterValue = {
  countryCode: string;
  stateCode: string;
  city: string;
};

export type EventScheduleFilterValue = {
  dateRange: DateRangeValue | null;
  eventTypes: Array<EventTypeFilterProp | EventTypeNotFilterProp>;
};

export type CustomProfileFieldFilterValue = {
  searchKey: string;
  kind: SearchProfileFieldKind;
  value: string[] | boolean | DateRangeValue | NumberRangeProps;
  aggregationKey?: string;
};

export type SearchProfileFieldKind =
  | 'single_line'
  | 'multi_line'
  | 'dropdown'
  | 'boolean'
  | 'date'
  | 'number'
  | 'salary';

export enum FilterValueType {
  Text,
  Boolean,
  DateRange,
  Number,
  Range,
  Status,
  GdprStatus,
  GdprConsentEverGiven,
  Source,
  BelongsToRelation,
  HasManyRelation,
  HasManyOffersRelation,
  OpenQuestions,
  QuestionnaireAnswers,
  EventDateRange,
  EventNotDateRange,
  JobStatus,
  Location,
  Gender,
  NotGender,
  CustomProfileField,
  EEOStatus,
  DisqualifyKind,
  HiringStatus,
}

export enum FilterType {
  GdprStatus = 'gdpr_status',
  GdprExpiresAt = 'gdpr_expires_at',
  GdprConsentEverGiven = 'gdpr_consent_ever_given',
  Query = 'query',
  Status = 'status',
  HasGdpr = 'gdpr',
  Source = 'source',
  Rating = 'rating',
  PositiveRatings = 'positive_ratings',
  Deleted = 'deleted',
  CoverLetter = 'cover_letter',
  CV = 'cv',
  CreatedAt = 'created_at',
  HasEmail = 'has_email',
  HasEmailReceivedFrom = 'has_email_received_from',
  HasntEmailReceivedFrom = 'hasnt_email_received_from',
  HasEmailSentTo = 'has_email_sent_to',
  HasntEmailSentTo = 'hasnt_email_sent_to',
  HasAdmin = 'has_admin',
  HasntAdmin = 'hasnt_admin',
  HasFollower = 'has_follower',
  HasntFollower = 'hasnt_follower',
  HasInterviewer = 'has_interviewer',
  HasntInterviewer = 'hasnt_interviewer',
  HasJobs = 'has_jobs',
  HasntJobs = 'hasnt_jobs',
  HasAllJobs = 'has_all_jobs',
  HasTalentPools = 'has_talent_pools',
  HasntTalentPools = 'hasnt_talent_pools',
  HasAllTalentPools = 'has_all_talent_pools',
  HasDisqualifyKind = 'has_disqualify_kind',
  HasDisqualifyReasons = 'has_disqualify_reasons',
  HasntDisqualifyReasons = 'hasnt_disqualify_reasons',
  HasStages = 'has_stages',
  HasntStages = 'hasnt_stages',
  HasTags = 'has_tags',
  HasntTags = 'hasnt_tags',
  HasAllTags = 'has_all_tags',
  HasSources = 'has_sources',
  HasntSources = 'hasnt_sources',
  HasAllSources = 'has_all_sources',
  HasOpenQuestions = 'has_open_questions',
  HasntOpenQuestions = 'hasnt_open_questions',
  GdprChangeRequestCreatedAt = 'gdpr_uncompleted_change_request_created_at',
  GdprRemovalRequestCreatedAt = 'gdpr_uncompleted_removal_request_created_at',
  HasEventScheduled = 'has_event_scheduled',
  HasntEventScheduled = 'hasnt_event_scheduled',
  SchoolNameContains = 'school_name_contains',
  SchoolNameNotContain = 'school_name_not_contain',
  DegreeContains = 'degree_contains',
  DegreeNotContain = 'degree_not_contain',
  MajorContains = 'major_contains',
  MajorNotContain = 'major_not_contain',
  CompanyNameContains = 'company_name_contains',
  CompanyNameNotContain = 'company_name_not_contain',
  JobTitleContains = 'job_title_contains',
  JobTitleNotContain = 'job_title_not_contain',
  Tasks = 'has_tasks',
  JobDepartmentIs = 'job_department_is',
  JobDepartmentIsNot = 'job_department_is_not',
  JobStatus = 'job_status',
  JobLocationAddressIs = 'job_location_address_is',
  JobLocationAddressIsnt = 'job_location_address_isnt',
  JobLocationIs = 'job_location_is',
  JobLocationIsnt = 'job_location_isnt',
  HasReceivedQuestionnaire = 'has_received_questionnaire',
  HasNotReceivedQuestionnaire = 'has_not_received_questionnaire',
  HasFilledQuestionnaire = 'has_filled_questionnaire',
  HasNotFilledQuestionnaire = 'has_not_filled_questionnaire',
  HasQuestionnaireAnswer = 'has_questionnaire_answer',
  HasntQuestionnaireAnswer = 'has_not_questionnaire_answer',
  GenderIs = 'gender_is',
  GenderIsNot = 'gender_is_not',
  NationalityIs = 'nationality_is',
  NationalityIsNot = 'nationality_is_not',
  LanguageIs = 'language_is',
  LanguageIsNot = 'language_is_not',
  LanguageIsAll = 'language_is_all',
  SkillIs = 'skill_is',
  SkillIsAll = 'skill_is_all',
  SkillIsNot = 'skill_is_not',
  AddressContains = 'address_contains',
  AddressNotContain = 'address_not_contain',
  ProfileFieldIs = 'custom_profile_field_is',
  ProfileFieldIsNot = 'custom_profile_field_is_not',
  Duplicates = 'duplicates',
  EEOStatus = 'eeo_data_status',
  JobFitScore = 'job_fit_score',
  AverageScore = 'average_score',
  HiringStatus = 'hiring_status',
  HiredAt = 'hired_at',
  JobStartsAt = 'job_starts_at',
  DateOfBirth = 'date_of_birth',
  HasWorkLocations = 'has_work_locations',
  HasntWorkLocations = 'hasnt_work_locations',
  HasJobLocationName = 'has_job_location_name',
  HasntJobLocationName = 'hasnt_job_location_name',
}

export type AggregationCounters = { [key: string]: AggregationCounterValue };
export type AggregationCounterValue = { [key: string]: number };

export const aggregationKeys: { [key: string]: string[] } = {
  [FilterType.HasEventScheduled]: ['events.kind.in'],
  [FilterType.HasntEventScheduled]: ['events.kind.not_in'],
  [FilterType.HasJobs]: ['jobs.id.in'],
  [FilterType.HasntJobs]: ['jobs.id.not_in'],
  [FilterType.HasAllJobs]: ['jobs.id.all_in'],
  [FilterType.HasTalentPools]: ['talent_pools.id.in'],
  [FilterType.HasntTalentPools]: ['talent_pools.id.not_in'],
  [FilterType.HasAllTalentPools]: ['talent_pools.id.all_in'],
  [FilterType.JobDepartmentIs]: ['jobs.department_id.in'],
  [FilterType.JobDepartmentIsNot]: ['jobs.department_id.not_in'],
  [FilterType.JobLocationIs]: ['jobs.country_code.in', 'jobs.state_code.in', 'jobs.city.in'],
  [FilterType.JobLocationIsnt]: [
    'jobs.country_code.not_in',
    'jobs.state_code.not_in',
    'jobs.city.not_in',
  ],
  [FilterType.JobLocationAddressIs]: ['jobs.country_code.in', 'jobs.state_code.in', 'jobs.city.in'],
  [FilterType.JobLocationAddressIsnt]: [
    'jobs.country_code.not_in',
    'jobs.state_code.not_in',
    'jobs.city.not_in',
  ],
  [FilterType.JobStatus]: ['jobs.status.in'],
  [FilterType.HasFilledQuestionnaire]: ['questionnaires_filled.template_name.in'],
  [FilterType.HasNotFilledQuestionnaire]: ['questionnaires_filled.template_name.not_in'],
  [FilterType.HasReceivedQuestionnaire]: ['questionnaires_received.template_name.in'],
  [FilterType.HasNotReceivedQuestionnaire]: ['questionnaires_received.template_name.not_in'],
  [FilterType.Tasks]: ['tasks.exists'],
  [FilterType.GenderIs]: ['gender.value.in'],
  [FilterType.GenderIsNot]: ['gender.value.not_in'],
  [FilterType.LanguageIs]: ['language_skill.language_code.in'],
  [FilterType.LanguageIsAll]: ['language_skill.language_code.has_all_of'],
  [FilterType.LanguageIsNot]: ['language_skill.language_code.not_in'],
  [FilterType.NationalityIs]: ['nationality.country_code.in'],
  [FilterType.NationalityIsNot]: ['nationality.country_code.not_in'],
  [FilterType.SkillIs]: ['skills.text.in'],
  [FilterType.SkillIsAll]: ['skills.text.has_all_of'],
  [FilterType.SkillIsNot]: ['skills.text.not_in'],
  [FilterType.ProfileFieldIs]: ['candidate_fields.search_key.in'],
  [FilterType.ProfileFieldIsNot]: ['candidate_fields.search_key.not_in'],
  [FilterType.Duplicates]: ['potential_duplicate.eq'],
  [FilterType.HasSources]: ['sources.id.in'],
  [FilterType.HasntSources]: ['sources.id.not_in'],
  [FilterType.HasAllSources]: ['sources.id.all_in'],
  [FilterType.HasDisqualifyReasons]: ['disqualifies.reason.in'],
  [FilterType.HasntDisqualifyReasons]: ['disqualifies.reason.not_in'],
  [FilterType.HasTags]: ['tags.id.in'],
  [FilterType.HasntTags]: ['tags.id.not_in'],
  [FilterType.HasAllTags]: ['tags.id.all_in'],
  [FilterType.HiringStatus]: ['hiring.status.in'],
  [FilterType.HasStages]: ['stages.name.in'],
  [FilterType.HasntStages]: ['stages.name.not_in'],
  [FilterType.HasFollower]: ['followers.id.in'],
  [FilterType.HasntFollower]: ['followers.id.not_in'],
  [FilterType.HasAdmin]: ['admin.id.in'],
  [FilterType.HasntAdmin]: ['admin.id.not_in'],
  [FilterType.HasWorkLocations]: ['locations.id.in'],
  [FilterType.HasntWorkLocations]: ['locations.id.not_in'],
  [FilterType.HasJobLocationName]: ['jobs.location_ids.in'],
  [FilterType.HasntJobLocationName]: ['jobs.location_ids.not_in'],
};

export type FilterTypeOptions = {
  filterValueType: FilterValueType;
  // Supported in BelongsTo, HasMany relation values
  // If true, it supports also an AnyFilterValue value
  hasAnyValueOption?: boolean;
  // Supported in BelongsTo, HasMany relation values
  // If true, values will be always converted to a number (unless it's an AnyFilterValue value)
  onlyNumberValues?: boolean;
};

export const filterTypesOptions: { [key: string]: FilterTypeOptions } = {
  [FilterType.Query]: {
    filterValueType: FilterValueType.Text,
  },
  [FilterType.Status]: {
    filterValueType: FilterValueType.Status,
  },
  [FilterType.HasGdpr]: {
    filterValueType: FilterValueType.Boolean,
  },
  [FilterType.GdprStatus]: {
    filterValueType: FilterValueType.GdprStatus,
  },
  [FilterType.GdprConsentEverGiven]: {
    filterValueType: FilterValueType.Boolean,
  },
  [FilterType.Source]: {
    filterValueType: FilterValueType.Source,
  },
  [FilterType.Rating]: {
    filterValueType: FilterValueType.Range,
  },
  [FilterType.PositiveRatings]: {
    filterValueType: FilterValueType.Range,
  },
  [FilterType.CoverLetter]: {
    filterValueType: FilterValueType.Boolean,
  },
  [FilterType.CV]: {
    filterValueType: FilterValueType.Boolean,
  },
  [FilterType.CreatedAt]: {
    filterValueType: FilterValueType.DateRange,
  },
  [FilterType.HiredAt]: {
    filterValueType: FilterValueType.DateRange,
  },
  [FilterType.JobStartsAt]: {
    filterValueType: FilterValueType.DateRange,
  },
  [FilterType.DateOfBirth]: {
    filterValueType: FilterValueType.DateRange,
  },
  [FilterType.HasEmail]: {
    filterValueType: FilterValueType.Boolean,
  },
  [FilterType.HasEmailReceivedFrom]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasntEmailReceivedFrom]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasEmailSentTo]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasntEmailSentTo]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasAdmin]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasntAdmin]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasFollower]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasntFollower]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasInterviewer]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasntInterviewer]: {
    filterValueType: FilterValueType.BelongsToRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasJobs]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasntJobs]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasAllJobs]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    onlyNumberValues: true,
  },
  [FilterType.HasTalentPools]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasntTalentPools]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasAllTalentPools]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    onlyNumberValues: true,
  },
  [FilterType.HasDisqualifyKind]: {
    filterValueType: FilterValueType.DisqualifyKind,
  },
  [FilterType.HasDisqualifyReasons]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasntDisqualifyReasons]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasStages]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasntStages]: {
    filterValueType: FilterValueType.HasManyOffersRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasTags]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasntTags]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasAllTags]: {
    filterValueType: FilterValueType.HasManyRelation,
    onlyNumberValues: true,
  },
  [FilterType.HasSources]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasntSources]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.HasAllSources]: {
    filterValueType: FilterValueType.HasManyRelation,
    onlyNumberValues: true,
  },
  [FilterType.HasOpenQuestions]: {
    filterValueType: FilterValueType.OpenQuestions,
  },
  [FilterType.HasntOpenQuestions]: {
    filterValueType: FilterValueType.OpenQuestions,
  },
  [FilterType.GdprChangeRequestCreatedAt]: {
    filterValueType: FilterValueType.Number,
  },
  [FilterType.GdprRemovalRequestCreatedAt]: {
    filterValueType: FilterValueType.Number,
  },
  [FilterType.GdprExpiresAt]: {
    filterValueType: FilterValueType.Number,
  },
  [FilterType.HasEventScheduled]: {
    filterValueType: FilterValueType.EventDateRange,
  },
  [FilterType.HasntEventScheduled]: {
    filterValueType: FilterValueType.EventNotDateRange,
  },
  [FilterType.SchoolNameContains]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.SchoolNameNotContain]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.DegreeContains]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.DegreeNotContain]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.MajorContains]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.MajorNotContain]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.CompanyNameContains]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.CompanyNameNotContain]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.JobTitleContains]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.JobTitleNotContain]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.Tasks]: {
    filterValueType: FilterValueType.Boolean,
  },
  [FilterType.JobDepartmentIs]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.JobDepartmentIsNot]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
    onlyNumberValues: true,
  },
  [FilterType.JobStatus]: {
    filterValueType: FilterValueType.JobStatus,
  },
  [FilterType.HiringStatus]: {
    filterValueType: FilterValueType.HiringStatus,
  },
  [FilterType.JobLocationIs]: {
    filterValueType: FilterValueType.Location,
  },
  [FilterType.JobLocationIsnt]: {
    filterValueType: FilterValueType.Location,
  },
  [FilterType.JobLocationAddressIs]: {
    filterValueType: FilterValueType.Location,
  },
  [FilterType.JobLocationAddressIsnt]: {
    filterValueType: FilterValueType.Location,
  },
  [FilterType.HasReceivedQuestionnaire]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.HasNotReceivedQuestionnaire]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.HasFilledQuestionnaire]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.HasNotFilledQuestionnaire]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.HasQuestionnaireAnswer]: {
    filterValueType: FilterValueType.QuestionnaireAnswers,
  },
  [FilterType.HasntQuestionnaireAnswer]: {
    filterValueType: FilterValueType.QuestionnaireAnswers,
  },
  [FilterType.NationalityIs]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.NationalityIsNot]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.LanguageIs]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.LanguageIsNot]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.LanguageIsAll]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.SkillIs]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.SkillIsAll]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.SkillIsNot]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.AddressContains]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.AddressNotContain]: {
    filterValueType: FilterValueType.HasManyRelation,
  },
  [FilterType.GenderIs]: {
    filterValueType: FilterValueType.Gender,
  },
  [FilterType.GenderIsNot]: {
    filterValueType: FilterValueType.NotGender,
  },
  [FilterType.ProfileFieldIs]: {
    filterValueType: FilterValueType.CustomProfileField,
  },
  [FilterType.ProfileFieldIsNot]: {
    filterValueType: FilterValueType.CustomProfileField,
  },
  [FilterType.Duplicates]: {
    filterValueType: FilterValueType.Boolean,
  },
  [FilterType.Deleted]: {
    filterValueType: FilterValueType.Boolean,
  },
  [FilterType.EEOStatus]: {
    filterValueType: FilterValueType.EEOStatus,
  },
  [FilterType.JobFitScore]: {
    filterValueType: FilterValueType.Range,
  },
  [FilterType.AverageScore]: {
    filterValueType: FilterValueType.Range,
  },
  [FilterType.HasWorkLocations]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasntWorkLocations]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasJobLocationName]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
  },
  [FilterType.HasntJobLocationName]: {
    filterValueType: FilterValueType.HasManyRelation,
    hasAnyValueOption: true,
  },
};

export type FilterGroup =
  | { filterType: FilterType.Query; filterValue: string }
  | { filterType: FilterType.Deleted; filterValue: { filter: 'deleted'; eq: true } }
  | { filterType: FilterType.Status; filterValue: StatusFilterProp[] }
  | { filterType: FilterType.GdprStatus; filterValue: GdprStatusFilterProp[] }
  | { filterType: FilterType.GdprConsentEverGiven; filterValue: boolean }
  | { filterType: FilterType.Source; filterValue: SourceFilterProp[] }
  | { filterType: FilterType.Rating; filterValue: NumberFilterValue }
  | { filterType: FilterType.PositiveRatings; filterValue: NumberFilterValue }
  | { filterType: FilterType.CoverLetter; filterValue: boolean | null }
  | { filterType: FilterType.CV; filterValue: boolean | null }
  | { filterType: FilterType.CreatedAt; filterValue: DateRangeValue | null }
  | { filterType: FilterType.HiredAt; filterValue: DateRangeValue | null }
  | { filterType: FilterType.JobStartsAt; filterValue: DateRangeValue | null }
  | { filterType: FilterType.HasEmail; filterValue: boolean | null }
  | { filterType: FilterType.HasEmailReceivedFrom; filterValue: string | AnyFilterValue | null }
  | { filterType: FilterType.HasntEmailReceivedFrom; filterValue: string | AnyFilterValue | null }
  | { filterType: FilterType.HasEmailSentTo; filterValue: string | AnyFilterValue | null }
  | { filterType: FilterType.HasntEmailSentTo; filterValue: string | AnyFilterValue | null }
  | { filterType: FilterType.HasAdmin; filterValue: number | AnyFilterValue | null }
  | { filterType: FilterType.HasntAdmin; filterValue: number | AnyFilterValue | null }
  | { filterType: FilterType.HasFollower; filterValue: number | AnyFilterValue | null }
  | { filterType: FilterType.HasntFollower; filterValue: number | AnyFilterValue | null }
  | { filterType: FilterType.HasInterviewer; filterValue: number | AnyFilterValue | null }
  | { filterType: FilterType.HasntInterviewer; filterValue: number | AnyFilterValue | null }
  | {
      filterType: FilterType.HasJobs;
      filterValue: Array<number | AnyFilterValue | AnyFollowedFilterValue>;
    }
  | {
      filterType: FilterType.HasntJobs;
      filterValue: Array<number | AnyFilterValue | AnyFollowedFilterValue>;
    }
  | { filterType: FilterType.HasAllJobs; filterValue: number[] }
  | {
      filterType: FilterType.HasTalentPools;
      filterValue: Array<number | AnyFilterValue | AnyFollowedFilterValue>;
    }
  | {
      filterType: FilterType.HasntTalentPools;
      filterValue: Array<number | AnyFilterValue | AnyFollowedFilterValue>;
    }
  | { filterType: FilterType.HasAllTalentPools; filterValue: number[] }
  | { filterType: FilterType.HasDisqualifyKind; filterValue: DisqualifyKind[] }
  | { filterType: FilterType.HasDisqualifyReasons; filterValue: Array<string | AnyFilterValue> }
  | { filterType: FilterType.HasntDisqualifyReasons; filterValue: Array<string | AnyFilterValue> }
  | { filterType: FilterType.HasStages; filterValue: Array<string | AnyFilterValue> }
  | { filterType: FilterType.HasntStages; filterValue: Array<string | AnyFilterValue> }
  | { filterType: FilterType.HasTags; filterValue: Array<number | AnyFilterValue> }
  | { filterType: FilterType.HasntTags; filterValue: Array<number | AnyFilterValue> }
  | { filterType: FilterType.HasAllTags; filterValue: number[] }
  | { filterType: FilterType.HasSources; filterValue: Array<number | AnyFilterValue> }
  | { filterType: FilterType.HasntSources; filterValue: Array<number | AnyFilterValue> }
  | { filterType: FilterType.HasAllSources; filterValue: number[] }
  | { filterType: FilterType.HasOpenQuestions; filterValue: OpenQuestionsFilterValue }
  | { filterType: FilterType.HasntOpenQuestions; filterValue: OpenQuestionsFilterValue }
  | { filterType: FilterType.GdprChangeRequestCreatedAt; filterValue: number }
  | { filterType: FilterType.GdprRemovalRequestCreatedAt; filterValue: number }
  | { filterType: FilterType.GdprExpiresAt; filterValue: number }
  | { filterType: FilterType.HasReceivedQuestionnaire; filterValue: string[] }
  | { filterType: FilterType.HasNotReceivedQuestionnaire; filterValue: string[] }
  | { filterType: FilterType.HasFilledQuestionnaire; filterValue: string[] }
  | { filterType: FilterType.HasNotFilledQuestionnaire; filterValue: string[] }
  | { filterType: FilterType.HasQuestionnaireAnswer; filterValue: QuestionnaireAnswersFilterValue }
  | {
      filterType: FilterType.HasntQuestionnaireAnswer;
      filterValue: QuestionnaireAnswersFilterValue;
    }
  | { filterType: FilterType.HasEventScheduled; filterValue: EventScheduleFilterValue }
  | { filterType: FilterType.HasntEventScheduled; filterValue: EventScheduleFilterValue }
  | { filterType: FilterType.SchoolNameContains; filterValue: string[] }
  | { filterType: FilterType.SchoolNameNotContain; filterValue: string[] }
  | { filterType: FilterType.DegreeContains; filterValue: string[] }
  | { filterType: FilterType.DegreeNotContain; filterValue: string[] }
  | { filterType: FilterType.MajorContains; filterValue: string[] }
  | { filterType: FilterType.MajorNotContain; filterValue: string[] }
  | { filterType: FilterType.CompanyNameContains; filterValue: string[] }
  | { filterType: FilterType.CompanyNameNotContain; filterValue: string[] }
  | { filterType: FilterType.JobTitleContains; filterValue: string[] }
  | { filterType: FilterType.JobTitleNotContain; filterValue: string[] }
  | { filterType: FilterType.Tasks; filterValue: boolean }
  | { filterType: FilterType.JobStatus; filterValue: JobStatusFilterProp[] }
  | { filterType: FilterType.HiringStatus; filterValue: HiringStatusFilterProp[] }
  | { filterType: FilterType.JobDepartmentIs; filterValue: Array<number | AnyFilterValue> }
  | { filterType: FilterType.JobDepartmentIsNot; filterValue: Array<number | AnyFilterValue> }
  | { filterType: FilterType.JobLocationIs; filterValue: JobLocationFilterValue }
  | { filterType: FilterType.JobLocationIsnt; filterValue: JobLocationFilterValue }
  | { filterType: FilterType.JobLocationAddressIs; filterValue: JobLocationFilterValue }
  | { filterType: FilterType.JobLocationAddressIsnt; filterValue: JobLocationFilterValue }
  | { filterType: FilterType.NationalityIs; filterValue: string[] }
  | { filterType: FilterType.NationalityIsNot; filterValue: string[] }
  | { filterType: FilterType.LanguageIs; filterValue: string[] }
  | { filterType: FilterType.LanguageIsNot; filterValue: string[] }
  | { filterType: FilterType.LanguageIsAll; filterValue: string[] }
  | { filterType: FilterType.SkillIs; filterValue: string[] }
  | { filterType: FilterType.SkillIsAll; filterValue: string[] }
  | { filterType: FilterType.SkillIsNot; filterValue: string[] }
  | { filterType: FilterType.AddressContains; filterValue: string[] }
  | { filterType: FilterType.AddressNotContain; filterValue: string[] }
  | { filterType: FilterType.GenderIs; filterValue: GenderFilterProp[] }
  | { filterType: FilterType.GenderIsNot; filterValue: NotGenderFilterProp[] }
  | { filterType: FilterType.ProfileFieldIs; filterValue: CustomProfileFieldFilterValue[] }
  | { filterType: FilterType.ProfileFieldIsNot; filterValue: CustomProfileFieldFilterValue[] }
  | { filterType: FilterType.Duplicates; filterValue: boolean | null }
  | { filterType: FilterType.EEOStatus; filterValue: EEOStatusFilterProp[] }
  | { filterType: FilterType.JobFitScore; filterValue: NumberFilterValue }
  | { filterType: FilterType.AverageScore; filterValue: NumberFilterValue }
  | { filterType: FilterType.DateOfBirth; filterValue: DateRangeValue | null }
  | { filterType: FilterType.HasWorkLocations; filterValue: string[] }
  | { filterType: FilterType.HasntWorkLocations; filterValue: string[] }
  | { filterType: FilterType.HasJobLocationName; filterValue: string[] }
  | { filterType: FilterType.HasntJobLocationName; filterValue: string[] };

export const newInterfaceFilters: FilterType[] = [
  FilterType.SchoolNameContains,
  FilterType.SchoolNameNotContain,
  FilterType.DegreeContains,
  FilterType.DegreeNotContain,
  FilterType.MajorContains,
  FilterType.MajorNotContain,
  FilterType.CompanyNameContains,
  FilterType.CompanyNameNotContain,
  FilterType.JobTitleContains,
  FilterType.JobTitleNotContain,
  FilterType.HasEventScheduled,
  FilterType.HasntEventScheduled,
  FilterType.Tasks,
  FilterType.JobStatus,
  FilterType.JobDepartmentIs,
  FilterType.JobDepartmentIsNot,
  FilterType.JobLocationAddressIs,
  FilterType.JobLocationAddressIsnt,
  FilterType.JobLocationIs,
  FilterType.JobLocationIsnt,
  FilterType.HasJobs,
  FilterType.HasntJobs,
  FilterType.HasAllJobs,
  FilterType.HasTalentPools,
  FilterType.HasAllTalentPools,
  FilterType.HasntTalentPools,
  FilterType.HasReceivedQuestionnaire,
  FilterType.HasNotReceivedQuestionnaire,
  FilterType.HasFilledQuestionnaire,
  FilterType.HasNotFilledQuestionnaire,
  FilterType.GenderIs,
  FilterType.GenderIsNot,
  FilterType.AddressContains,
  FilterType.AddressNotContain,
  FilterType.LanguageIs,
  FilterType.LanguageIsAll,
  FilterType.LanguageIsNot,
  FilterType.NationalityIs,
  FilterType.NationalityIsNot,
  FilterType.SkillIs,
  FilterType.SkillIsAll,
  FilterType.SkillIsNot,
  FilterType.ProfileFieldIs,
  FilterType.ProfileFieldIsNot,
  FilterType.Duplicates,
  FilterType.HasSources,
  FilterType.HasntSources,
  FilterType.HasAllSources,
  FilterType.HasTags,
  FilterType.HasntTags,
  FilterType.HasAllTags,
  FilterType.HasStages,
  FilterType.HasntStages,
  FilterType.HasDisqualifyReasons,
  FilterType.HasntDisqualifyReasons,
  FilterType.HiringStatus,
  FilterType.HiredAt,
  FilterType.JobStartsAt,
  FilterType.HasFollower,
  FilterType.HasntFollower,
  FilterType.HasAdmin,
  FilterType.HasntAdmin,
  FilterType.HasWorkLocations,
  FilterType.HasntWorkLocations,
  FilterType.HasJobLocationName,
  FilterType.HasntJobLocationName,
];

export enum FilterParent {
  Education = 'education',
  Experience = 'experience',
  Events = 'events',
  Tasks = 'tasks',
  Job = 'job',
  TalentPools = 'talent_pools',
  QuestionnairesReceived = 'questionnaires_received',
  QuestionnairesFilled = 'questionnaires_filled',
  Gender = 'gender',
  Address = 'address',
  Language = 'language',
  Nationality = 'nationality',
  Skill = 'skill',
  CustomProfileField = 'custom_profile_field',
  Duplicates = 'duplicates',
  Sources = 'sources',
  Tags = 'tags',
  DisqualifyReasons = 'disqualify_reasons',
  Stages = 'stages',
  Hires = 'hires',
  Followers = 'followers',
  Admin = 'admin',
  WorkLocations = 'work_locations',
}

export const filterParentGroups: { [key: string]: FilterType[] } = {
  [FilterParent.Education]: [
    FilterType.SchoolNameContains,
    FilterType.SchoolNameNotContain,
    FilterType.DegreeContains,
    FilterType.DegreeNotContain,
    FilterType.MajorContains,
    FilterType.MajorNotContain,
  ],
  [FilterParent.Experience]: [
    FilterType.CompanyNameContains,
    FilterType.CompanyNameNotContain,
    FilterType.JobTitleContains,
    FilterType.JobTitleNotContain,
  ],
  [FilterParent.Events]: [FilterType.HasEventScheduled, FilterType.HasntEventScheduled],
  [FilterParent.Tasks]: [FilterType.Tasks],
  [FilterParent.Job]: [
    FilterType.JobStatus,
    FilterType.JobDepartmentIs,
    FilterType.JobDepartmentIsNot,
    FilterType.JobLocationIs,
    FilterType.JobLocationIsnt,
    FilterType.JobLocationAddressIs,
    FilterType.JobLocationAddressIsnt,
    FilterType.HasJobs,
    FilterType.HasntJobs,
    FilterType.HasAllJobs,
    FilterType.HasJobLocationName,
    FilterType.HasntJobLocationName,
  ],
  [FilterParent.TalentPools]: [
    FilterType.HasTalentPools,
    FilterType.HasntTalentPools,
    FilterType.HasAllTalentPools,
  ],
  [FilterParent.QuestionnairesReceived]: [
    FilterType.HasReceivedQuestionnaire,
    FilterType.HasNotReceivedQuestionnaire,
  ],
  [FilterParent.QuestionnairesFilled]: [
    FilterType.HasFilledQuestionnaire,
    FilterType.HasNotFilledQuestionnaire,
  ],
  [FilterParent.Gender]: [FilterType.GenderIs, FilterType.GenderIsNot],
  [FilterParent.Address]: [FilterType.AddressContains, FilterType.AddressNotContain],
  [FilterParent.Language]: [
    FilterType.LanguageIs,
    FilterType.LanguageIsAll,
    FilterType.LanguageIsNot,
  ],
  [FilterParent.Nationality]: [FilterType.NationalityIs, FilterType.NationalityIsNot],
  [FilterParent.Skill]: [FilterType.SkillIs, FilterType.SkillIsAll, FilterType.SkillIsNot],
  [FilterParent.CustomProfileField]: [FilterType.ProfileFieldIs, FilterType.ProfileFieldIsNot],
  [FilterParent.Duplicates]: [FilterType.Duplicates],
  [FilterParent.Sources]: [
    FilterType.HasSources,
    FilterType.HasntSources,
    FilterType.HasAllSources,
  ],
  [FilterParent.Tags]: [FilterType.HasTags, FilterType.HasntTags, FilterType.HasAllTags],
  [FilterParent.Stages]: [FilterType.HasStages, FilterType.HasntStages],
  [FilterParent.DisqualifyReasons]: [
    FilterType.HasDisqualifyReasons,
    FilterType.HasntDisqualifyReasons,
  ],
  [FilterParent.Hires]: [FilterType.HiringStatus, FilterType.HiredAt, FilterType.JobStartsAt],
  [FilterParent.Followers]: [FilterType.HasFollower, FilterType.HasntFollower],
  [FilterParent.Admin]: [FilterType.HasAdmin, FilterType.HasntAdmin],
  [FilterParent.WorkLocations]: [FilterType.HasWorkLocations, FilterType.HasntWorkLocations],
};
export type FilterIconMap = Map<FilterType, IconName>;

export const searchFilterIcons: FilterIconMap = new Map<FilterType, IconName>([
  [FilterType.PositiveRatings, 'thumbs-up-down'],
  [FilterType.HasAllJobs, 'work'],
  [FilterType.HasJobs, 'work'],
  [FilterType.HasntJobs, 'work'],
  [FilterType.HasStages, 'pipeline'],
  [FilterType.HasntStages, 'pipeline'],
  [FilterType.HasAllTags, 'label'],
  [FilterType.HasTags, 'label'],
  [FilterType.HasntTags, 'label'],
  [FilterType.HasAllSources, 'tag-text-outline'],
  [FilterType.HasSources, 'tag-text-outline'],
  [FilterType.HasntSources, 'tag-text-outline'],
  [FilterType.CreatedAt, 'access-time'],
  [FilterType.HiredAt, 'access-time'],
  [FilterType.JobStartsAt, 'access-time'],
  [FilterType.HasDisqualifyKind, 'block'],
  [FilterType.HasDisqualifyReasons, 'block'],
  [FilterType.HasntDisqualifyReasons, 'block'],
  [FilterType.Status, 'sync'],
  [FilterType.HasGdpr, 'gdpr-candidate'],
  [FilterType.Source, 'routes'],
  [FilterType.Rating, 'star'],
  [FilterType.CoverLetter, 'file-document'],
  [FilterType.CV, 'file-account'],
  [FilterType.HasEmail, 'mail'],
  [FilterType.HasEmailReceivedFrom, 'mail'],
  [FilterType.HasEmailSentTo, 'mail'],
  [FilterType.HasntEmailReceivedFrom, 'mail'],
  [FilterType.HasntEmailSentTo, 'mail'],
  [FilterType.HasAdmin, 'people'],
  [FilterType.HasntAdmin, 'people'],
  [FilterType.HasFollower, 'people'],
  [FilterType.HasntFollower, 'people'],
  [FilterType.HasInterviewer, 'people'],
  [FilterType.HasntInterviewer, 'people'],
  [FilterType.HasTalentPools, 'nav-talent-pools-outline'],
  [FilterType.HasntTalentPools, 'nav-talent-pools-outline'],
  [FilterType.HasAllTalentPools, 'nav-talent-pools-outline'],
  [FilterType.HasOpenQuestions, 'assignment'],
  [FilterType.HasntOpenQuestions, 'assignment'],
  [FilterType.JobDepartmentIs, 'location-city'],
  [FilterType.JobDepartmentIsNot, 'location-city'],
  [FilterType.SchoolNameContains, 'book-open-page-variant'],
  [FilterType.SchoolNameNotContain, 'book-open-page-variant'],
  [FilterType.DegreeContains, 'book-open-page-variant'],
  [FilterType.DegreeNotContain, 'book-open-page-variant'],
  [FilterType.MajorContains, 'book-open-page-variant'],
  [FilterType.MajorNotContain, 'book-open-page-variant'],
  [FilterType.CompanyNameContains, 'fa-suitcase'],
  [FilterType.CompanyNameNotContain, 'fa-suitcase'],
  [FilterType.JobTitleContains, 'fa-suitcase'],
  [FilterType.JobTitleNotContain, 'fa-suitcase'],
  [FilterType.HasEventScheduled, 'timetable'],
  [FilterType.HasntEventScheduled, 'timetable'],
  [FilterType.Tasks, 'assignment-turned-in'],
  [FilterType.JobStatus, 'dot'],
  [FilterType.JobLocationAddressIs, 'place'],
  [FilterType.JobLocationAddressIsnt, 'place'],
  [FilterType.JobLocationIs, 'place'],
  [FilterType.JobLocationIsnt, 'place'],
  [FilterType.HasReceivedQuestionnaire, 'question-answer'],
  [FilterType.HasNotReceivedQuestionnaire, 'question-answer'],
  [FilterType.HasFilledQuestionnaire, 'question-answer'],
  [FilterType.HasNotFilledQuestionnaire, 'question-answer'],
  [FilterType.HasQuestionnaireAnswer, 'question-answer'],
  [FilterType.HasntQuestionnaireAnswer, 'question-answer'],
  [FilterType.GenderIs, 'wc'],
  [FilterType.GenderIsNot, 'wc'],
  [FilterType.NationalityIs, 'flag'],
  [FilterType.NationalityIsNot, 'flag'],
  [FilterType.LanguageIs, 'translate'],
  [FilterType.LanguageIsAll, 'translate'],
  [FilterType.LanguageIsNot, 'translate'],
  [FilterType.SkillIs, 'label'],
  [FilterType.SkillIsAll, 'label'],
  [FilterType.SkillIsNot, 'label'],
  [FilterType.AddressContains, 'my-location'],
  [FilterType.AddressNotContain, 'my-location'],
  [FilterType.ProfileFieldIs, 'assignment'],
  [FilterType.ProfileFieldIsNot, 'assignment'],
  [FilterType.Duplicates, 'content-copy'],
  [FilterType.GdprStatus, 'gdpr-candidate'],
  [FilterType.GdprExpiresAt, 'calendar-clock'],
  [FilterType.EEOStatus, 'non-federal-company'],
  [FilterType.JobFitScore, 'thumbs-up-down'],
  [FilterType.AverageScore, 'thumbs-up-down'],
  [FilterType.HiringStatus, 'hired'],
  [FilterType.DateOfBirth, 'cake-birthday'],
  [FilterType.HasWorkLocations, 'place'],
  [FilterType.HasntWorkLocations, 'place'],
  [FilterType.HasJobLocationName, 'place'],
  [FilterType.HasntJobLocationName, 'place'],
]);

export interface SearchQueryParamsOptions {
  view: ViewType;
  query: string;
  filters: FilterGroup[];
  offset: number;
  limit: number;
  sortBy: SortByType;
  queryParams: any;
}

export const getAggregationKeyForProfileFieldFilter = (
  searchKey: string,
  kind: SearchProfileFieldKind,
  contains: boolean,
): string => {
  switch (kind) {
    case 'dropdown':
      return `${searchKey}.value.${contains ? 'in' : 'not_in'}`;
    case 'boolean':
      return `${searchKey}.flag.${contains ? 'eq' : 'not_eq'}`;
    default:
      return '';
  }
};

export const booleanFilterValues = [
  FilterType.CoverLetter,
  FilterType.CV,
  FilterType.HasEmail,
  FilterType.Tasks,
  FilterType.Duplicates,
];
